/** The preflight glyph vocabulary — one definition, two screens, plus a legend.
 *
 *  The setup rail and the run board each carried their own glyph table, and
 *  between them showed seven shapes with no legend anywhere: an operator had to
 *  hover each one to learn what it meant, on the two screens where the
 *  vocabulary is load-bearing. Defining the shapes once keeps the rail and the
 *  board from drifting, and lets both render the same legend.
 *
 *  Never colour alone: every glyph has a distinct shape and a label.
 */
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { Box, CircularProgress, Tooltip, Typography } from '@mui/material';
import type { ReactElement } from 'react';

import type { PreflightItem } from '@/api/preflight';

export type PreflightGlyph =
  | 'done'
  | 'running'
  | 'error'
  | 'attention'
  | 'blocked'
  | 'human'
  | 'ready'
  | 'edit';

interface GlyphDef {
  node: ReactElement;
  label: string;
  /** What the operator should do about it — the part a bare label leaves out. */
  hint: string;
}

export const GLYPHS: Record<PreflightGlyph, GlyphDef> = {
  done: {
    node: <CheckCircleIcon fontSize="small" color="success" />,
    label: 'Done',
    hint: 'nothing left to do here',
  },
  running: { node: <CircularProgress size={16} />, label: 'Running', hint: 'in flight right now' },
  error: {
    node: <ErrorOutlineIcon fontSize="small" color="error" />,
    label: 'Error',
    hint: 'will not retry on its own — fix and re-run',
  },
  attention: {
    node: <EditOutlinedIcon fontSize="small" color="primary" />,
    label: 'Needs input',
    hint: 'waiting on a value from you',
  },
  blocked: {
    node: <LockOutlinedIcon fontSize="small" sx={{ color: 'text.disabled' }} />,
    label: 'Blocked',
    hint: 'starts by itself once its prerequisite completes',
  },
  human: {
    node: <PersonOutlineIcon fontSize="small" sx={{ color: 'text.secondary' }} />,
    label: 'Human action',
    hint: 'the work itself is done by a person',
  },
  ready: {
    node: <RadioButtonUncheckedIcon fontSize="small" sx={{ color: 'text.disabled' }} />,
    label: 'Ready',
    hint: 'the setup run will pick this up',
  },
  edit: {
    node: <EditOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />,
    label: 'Editable',
    hint: 'a form you can revisit at any time',
  },
};

/** The one glyph that describes an item, most-urgent first. */
export function itemGlyph(item: PreflightItem): PreflightGlyph {
  if (item.status === 'completed') return 'done';
  if (item.status === 'error') return 'error';
  if (item.status === 'started') return 'running';
  if (!item.ready) return 'attention';
  if (item.blocked_by.length > 0) return 'blocked';
  if (item.input_kind === 'human_action') return 'human';
  return 'ready';
}

/** A glyph with its tooltip, as the rail and the board render it inline. */
export function Glyph({ kind }: { kind: PreflightGlyph }) {
  const g = GLYPHS[kind];
  return (
    <Tooltip title={`${g.label} — ${g.hint}`}>
      <Box sx={{ display: 'flex' }} aria-label={g.label}>
        {g.node}
      </Box>
    </Tooltip>
  );
}

/** Legend for the glyphs actually on screen — listing the whole vocabulary
 *  would be longer than it is useful, and most of it absent. */
export function GlyphLegend({
  kinds,
  direction = 'row',
}: {
  kinds: PreflightGlyph[];
  direction?: 'row' | 'column';
}) {
  const seen = (Object.keys(GLYPHS) as PreflightGlyph[]).filter((k) => kinds.includes(k));
  if (seen.length === 0) return null;
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: direction,
        flexWrap: direction === 'row' ? 'wrap' : 'nowrap',
        gap: direction === 'row' ? 1.5 : 0.5,
        alignItems: direction === 'row' ? 'center' : 'flex-start',
      }}
    >
      {seen.map((k) => (
        <Box key={k} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
          <Box sx={{ display: 'flex', transform: 'scale(0.85)' }}>{GLYPHS[k].node}</Box>
          <Typography variant="caption" color="text.secondary" noWrap>
            {GLYPHS[k].label}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}
